import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Ticket, Loader2, Trash2, Plus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { loadTeamSenders } from '../components/SenderSelect';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { code: '', type: 'percent', value: '', applies_to: 'all', expires_at: '', max_uses: '', note: '' };
const SCOPE = { all: 'Subscription + add-ons', subscription: 'Subscription only', addons: 'Add-ons only' };
const inp = 'bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-[#ffffff] placeholder:text-slate-500 w-full';

const payload = (c) => ({
  code: c.code, type: c.type, value: Number(c.value), applies_to: c.applies_to, note: c.note || '',
  expires_at: c.expires_at || null, max_uses: c.max_uses ? Number(c.max_uses) : null, active: c.active ?? true,
});

const CodeForm = ({ onCreated }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await axios.post(`${API}/admin/discount-codes`, payload(f)); toast.success(`Code ${f.code.toUpperCase()} created`); setF(EMPTY); onCreated(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Could not create code'); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="grid grid-cols-2 md:grid-cols-7 gap-2 items-end" data-testid="discount-form">
      <label className="text-xs text-slate-400 col-span-2 md:col-span-1">Code<input value={f.code} onChange={set('code')} placeholder="LAUNCH20" className={`${inp} uppercase mt-1`} data-testid="dc-code" /></label>
      <label className="text-xs text-slate-400">Type
        <select value={f.type} onChange={set('type')} className={`${inp} mt-1`} data-testid="dc-type"><option value="percent">% off</option><option value="fixed">£ off</option></select></label>
      <label className="text-xs text-slate-400">{f.type === 'percent' ? 'Percent' : 'Amount (£)'}<input type="number" min="0" step="0.01" value={f.value} onChange={set('value')} className={`${inp} mt-1`} data-testid="dc-value" /></label>
      <label className="text-xs text-slate-400">Applies to
        <select value={f.applies_to} onChange={set('applies_to')} className={`${inp} mt-1`} data-testid="dc-applies-to">
          {Object.entries(SCOPE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
      <label className="text-xs text-slate-400">Expires (optional)<input type="date" value={f.expires_at} onChange={set('expires_at')} className={`${inp} mt-1`} data-testid="dc-expires" /></label>
      <label className="text-xs text-slate-400">Usage limit (optional)<input type="number" min="1" value={f.max_uses} onChange={set('max_uses')} placeholder="Unlimited" className={`${inp} mt-1`} data-testid="dc-max-uses" /></label>
      <button type="submit" disabled={busy || !f.code || !f.value} data-testid="dc-create"
        className="inline-flex items-center justify-center gap-1.5 bg-[#00A79D] hover:bg-[#00857D] text-[#ffffff] text-sm font-semibold rounded-md px-3 py-2 disabled:opacity-50 h-[38px]">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create
      </button>
    </form>
  );
};

const status = (c) => {
  if (!c.active) return ['Off', 'text-slate-400'];
  if (c.expires_at && c.expires_at < new Date().toISOString().slice(0, 10)) return ['Expired', 'text-amber-400'];
  if (c.max_uses && c.uses >= c.max_uses) return ['Used up', 'text-amber-400'];
  return ['Active', 'text-emerald-400'];
};

// Which supplier sizes may buy self-serve; larger bands see "Book a call with <person>".
const SelfServeLimit = () => {
  const [g, setG] = useState(null);
  const [team, setTeam] = useState([]);
  useEffect(() => {
    axios.get(`${API}/admin/sales-gate`).then((r) => setG(r.data)).catch(() => {});
    loadTeamSenders().then((d) => setTeam(d.items || []));
  }, []);
  const save = async (patch) => {
    const next = { max_band: g.max_band, member_id: g.member_id, booking_page_id: g.booking_page_id, booking_url: g.booking_url_override, sales_email: g.sales_email, ...patch };
    try { const r = await axios.put(`${API}/admin/sales-gate`, next); setG(r.data); toast.success('Saved'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };
  const createPage = async () => {
    try { const r = await axios.post(`${API}/admin/sales-gate/booking-page`); setG(r.data); toast.success('Booking page created'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not create page'); }
  };
  if (!g) return null;
  const sel = 'bg-white border border-slate-300 rounded-md px-3 py-2 text-sm text-slate-900';
  return (
    <Card className="bg-slate-900/50 border-slate-800" data-testid="self-serve-limit">
      <CardHeader><CardTitle className="text-sm text-slate-300">Self-serve limit</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-slate-500">Suppliers above this size can't pay online on the claim or create-listing checkout. They see "Book a call" instead, which opens this person's live booking page.</p>
        <div className="flex flex-wrap gap-3 items-center text-sm text-slate-400">
          <span>Self-serve up to</span>
          <select value={g.max_band} onChange={(e) => save({ max_band: e.target.value })} className={sel} data-testid="ssl-band">
            {g.bands.map((b) => <option key={b.key} value={b.key}>{`${b.label} (${b.staff_label} staff)`}</option>)}
          </select>
          <span>Calls go to</span>
          <select value={g.member_id || ''} onChange={(e) => save({ member_id: e.target.value || null, booking_page_id: null })} className={sel} data-testid="ssl-member">
            <option value="">Our team (no booking link)</option>
            {team.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        {g.member_id && <BookingSetup key={g.member_id} g={g} save={save} createPage={createPage} sel={sel} />}
      </CardContent>
    </Card>
  );
};

const SalesEmail = ({ g, save, sel }) => {
  const [v, setV] = useState(g.sales_email || '');
  return (
    <div className="flex flex-wrap items-center gap-2 text-slate-400">
      <span>Sales email shown above the link</span>
      <input value={v} onChange={(e) => setV(e.target.value)} className={`${sel} min-w-[260px]`} data-testid="ssl-sales-email" />
      <button type="button" onClick={() => save({ sales_email: v.trim() })} data-testid="ssl-sales-email-save"
        className="border border-slate-600 text-slate-200 hover:bg-slate-800 text-xs font-semibold rounded-md px-3 py-2 transition-colors">Save email</button>
    </div>
  );
};

const BookingSetup = ({ g, save, createPage, sel }) => {
  const [url, setUrl] = useState(g.booking_url_override || '');
  const who = g.contact_name;
  return (
    <div className="border border-slate-800 rounded-lg p-4 space-y-3 text-sm" data-testid="ssl-booking-setup">
      <div className="flex flex-wrap items-center gap-3 text-slate-400">
        <span>Booking page</span>
        {g.pages.length ? (
          <select value={g.booking_page_id || ''} onChange={(e) => save({ booking_page_id: e.target.value })} className={sel} data-testid="ssl-page">
            {g.pages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        ) : (
          <button type="button" onClick={createPage} data-testid="ssl-create-page"
            className="bg-[#00A79D] hover:bg-[#00857D] text-white text-xs font-semibold rounded-md px-3 py-2 transition-colors">Create a booking page for {who}</button>
        )}
        {g.booking_page_id && <a href={`/m/${g.booking_page_id}`} target="_blank" rel="noopener noreferrer" className="text-xs text-[#00A79D] underline" data-testid="ssl-page-link">Preview</a>}
      </div>
      <p className="text-xs" data-testid="ssl-calendar-status">
        {!g.ms_configured ? <span className="text-amber-500">Microsoft calendar app not set up yet. Slots follow working hours only until the Azure app details are added.</span>
          : g.calendar_connected ? <span className="text-emerald-400">{who}'s Outlook / Teams calendar is connected ({g.calendar_email}). Slots show her live availability and each booking gets a Teams link.</span>
            : <span className="text-amber-500">{who} hasn't connected her Outlook calendar yet. She can do it from Meetings &gt; Connect Outlook.</span>}
        {!g.calendar_live && !g.booking_url_override && g.booking_page_id && <span className="block text-slate-400 mt-1" data-testid="ssl-contact-form-note">Until the calendar is connected, the booking page offers slots in working hours and emails each booking to {g.sales_email || who}.</span>}
      </p>
      <SalesEmail g={g} save={save} sel={sel} />
      <div className="flex flex-wrap items-center gap-2">
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Optional: paste a Microsoft Bookings or other booking link" data-testid="ssl-override-url"
          className={`${sel} flex-1 min-w-[260px]`} />
        <button type="button" onClick={() => save({ booking_url: url.trim() })} data-testid="ssl-override-save"
          className="border border-slate-600 text-slate-200 hover:bg-slate-800 text-xs font-semibold rounded-md px-3 py-2 transition-colors">Save link</button>
      </div>
      <p className="text-xs text-slate-500">A pasted link replaces the booking page above. Leave it empty to use the booking page.</p>
    </div>
  );
};

export default function DiscountCodes() {
  const [items, setItems] = useState(null);
  const load = () => axios.get(`${API}/admin/discount-codes`).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const toggle = async (c) => {
    try { await axios.put(`${API}/admin/discount-codes/${c.id}`, payload({ ...c, active: !c.active, expires_at: c.expires_at || '' })); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not update'); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete code ${c.code}? It will stop working immediately.`)) return;
    await axios.delete(`${API}/admin/discount-codes/${c.id}`); toast.success('Code deleted'); load();
  };

  return (
    <div className="space-y-6" data-testid="discount-codes-page">
      <div>
        <h1 className="text-3xl font-bold text-[#ffffff] flex items-center gap-2"><Ticket className="w-7 h-7 text-[#00A79D]" /> Discount codes</h1>
        <p className="text-slate-400 mt-1 text-sm">Codes work on the self-serve Create a listing form and on claim checkout. Discounts apply to the first payment; a use is counted when payment completes.</p>
      </div>
      <SelfServeLimit />
      <Card className="bg-slate-900/50 border-slate-800"><CardHeader><CardTitle className="text-sm text-slate-300">New code</CardTitle></CardHeader>
        <CardContent><CodeForm onCreated={load} /></CardContent></Card>
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-0">
          {!items ? <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-500" /></div>
            : items.length === 0 ? <p className="text-sm text-slate-500 p-6" data-testid="dc-empty">No discount codes yet.</p> : (
              <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead><tr className="border-b border-slate-800 text-xs text-slate-500 uppercase tracking-wider">
                  {['Code', 'Discount', 'Applies to', 'Expires', 'Used', 'Status', 'On', ''].map((h) => <th key={h} className="text-left py-2 px-4 font-medium">{h}</th>)}
                </tr></thead>
                <tbody>
                  {items.map((c) => {
                    const [st, cls] = status(c);
                    return (
                      <tr key={c.id} className="border-b border-slate-800/60" data-testid={`dc-row-${c.code}`}>
                        <td className="py-2.5 px-4 font-mono font-semibold text-[#ffffff]">{c.code}</td>
                        <td className="py-2.5 px-4 text-slate-300">{c.type === 'percent' ? `${c.value}%` : `£${c.value}`} off</td>
                        <td className="py-2.5 px-4 text-slate-300">{SCOPE[c.applies_to]}</td>
                        <td className="py-2.5 px-4 text-slate-300">{c.expires_at ? new Date(c.expires_at).toLocaleDateString('en-GB') : 'Never'}</td>
                        <td className="py-2.5 px-4 font-mono text-slate-300" data-testid={`dc-uses-${c.code}`}>{c.uses || 0}{c.max_uses ? ` / ${c.max_uses}` : ''}</td>
                        <td className={`py-2.5 px-4 font-semibold ${cls}`} data-testid={`dc-status-${c.code}`}>{st}</td>
                        <td className="py-2.5 px-4"><Switch checked={!!c.active} onCheckedChange={() => toggle(c)} data-testid={`dc-toggle-${c.code}`} /></td>
                        <td className="py-2.5 px-4"><button onClick={() => remove(c)} className="text-slate-500 hover:text-red-400" data-testid={`dc-delete-${c.code}`}><Trash2 className="w-4 h-4" /></button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table></div>
            )}
        </CardContent>
      </Card>
    </div>
  );
}
